<template>
  <div id="contract-tab">
    <table>
      <thead>
        <tr>
          <th colspan="2">{{ $t('CNTRCT_ID') }}</th>
          <th>{{ $t('CREATED_DATE') }}</th>
          <th>{{ $t('MATURE_DATE') }}</th>
          <th>{{ $t('ASSET') }}</th>
          <th>{{ $t('LOAN_AMOUNT') }}</th>
          <th>{{ $t('INTEREST_RATE') }}</th>
          <th>{{ $t('STORAGE') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr :key="key" v-for="(item, key) in contracts" @click="navigateContractDetail(item.finfluxId)">
          <td
            class="status"
            :class="{
              'pending': (item.status === CONTRACT_STATUSES.PENDING),
              'approved': (item.status === CONTRACT_STATUSES.APPROVED),
              'disbursed': (item.status === CONTRACT_STATUSES.DISBURSED),
              'closed': (item.status === CONTRACT_STATUSES.CLOSED),
              'overdue': (item.status === CONTRACT_STATUSES.OVERDUE),
              'rejected': (item.status === CONTRACT_STATUSES.REJECTED)
            }"
          ><div class="bullet">•</div></td>
          <td
            class="status"
            :class="{
              'pending': (item.status === CONTRACT_STATUSES.PENDING),
              'approved': (item.status === CONTRACT_STATUSES.APPROVED),
              'disbursed': (item.status === CONTRACT_STATUSES.DISBURSED),
              'closed': (item.status === CONTRACT_STATUSES.CLOSED),
              'overdue': (item.status === CONTRACT_STATUSES.OVERDUE),
              'rejected': (item.status === CONTRACT_STATUSES.REJECTED)
            }"
          >
            <router-link :to="applyParams(PATHS.CONTRACT_DETAIL, item.finfluxId)">{{ item.finfluxId }}</router-link>
            <br />
            <small>{{ $t(item.status) }}</small>
          </td>
          <td>{{ formatDate(item.submittedOn) }}</td>
          <td>{{ formatDate(item.maturesOn) }}</td>
          <td class="asset" v-html="getFullDescription(item.asset)" />
          <td>{{ addCommas(item.approvedAmount) }}</td>
          <td>{{ item.interestRate }}</td>
          <td>{{ item.asset.storage.name }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import moment from 'moment';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import { CONTRACT_STATUSES, PATHS, DISPLAY_DATE_FORMAT } from '@/consts';
import { addCommas, applyParams, camelToUpperSnake } from '@/helpers';

const { useState } = createNamespacedHelpers('clientDetail');

export default {
  name: 'ContractTab',
  components: {},
  props: {
    showContractActive: Boolean
  },
  setup(props, { root }) {
    const { contracts } = useState(['contracts']);

    const navigateContractDetail = (id) => {
      root.$router.push({ name: 'contract-detail', params: { id } }).catch(() => {});
    };

    const formatDate = (date) => {
      const formatedDate = moment(date, 'YYYY-MM-DD');
      if (formatedDate.isValid()) {
        return formatedDate.format('DD/MM/YYYY');
      }
      return '';
    };

    const getFullDescription = (asset) => {
      let desc = asset.description;

      Object.keys(asset.meta || {}).forEach((key) => {
        if (asset.meta[key]) {
          desc += `<br/><span style="color:#BCBCBC">${root.$t(camelToUpperSnake(key))}:</span> <span>${asset.meta[key]}</span>`;
        }
      });

      return desc;
    };

    return {
      moment,
      DISPLAY_DATE_FORMAT,
      addCommas,
      applyParams,
      CONTRACT_STATUSES,
      PATHS,
      contracts,
      navigateContractDetail,
      formatDate,
      getFullDescription
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/colors";

#contract-tab {
  border-radius: 3px;

  table {
    width: 100%;
    border-collapse: inherit;
    border-spacing: 0;
    background-color: $ghostWhite;

    thead {
      text-transform: uppercase;
      font-weight: bold;
      text-align: center;

      tr {

        th {
          background-color: $brightMaroon;
          color: $white;
          padding: 15px 10px;
          border-top: 1px solid $alto;
          border-bottom: 1px solid $alto;
          font-size: 14px;

          &:first-child {
            border-top-left-radius: 3px;
            border-bottom-left-radius: 3px;
            border-left: 1px solid $alto;

          }

          &:last-child {
            border-top-right-radius: 3px;
            border-bottom-right-radius: 3px;
            border-right: 1px solid $alto;
          }
        }
      }
    }

    tbody {
      text-align: center;

      tr {
        background-color: $babyPowder;
        transition: all .2s ease-in-out;

        &:hover {
          background-color: $ghostWhite;
        }

        td {
          padding: 15px 5px;
          border-top: 1px solid $alto;
          border-bottom: 1px solid $alto;

          &:first-child {
            border-top-left-radius: 3px;
            border-bottom-left-radius: 3px;
            border-left: 1px solid $alto;
          }

          &:last-child {
            border-top-right-radius: 3px;
            border-bottom-right-radius: 3px;
            border-right: 1px solid $alto;
          }

          .icon {
            margin-right: 10px;
            cursor: pointer;
          }

          &.asset {
            max-width: 200px;
            text-align: left;
          }

          a {
            color: inherit;
          }

          &.status {
            color: $independence;
            line-height: 18px;

            .bullet {
              font-size: 36px;
              padding-right: 0;
              padding-left: 10px;
              transform: translateY(-2px);
              vertical-align: middle;
              display: inline-block;
            }

            &.disbursed {
              color: $shamrockGreen;
            }

            &.closed, &.canceled {
              color: $independence;
            }

            &.overdue {
              color: $geranium;
            }

            &.rejected {
              color: $lavenderGrey;
            }

            &.pending {
              color: $yellowCrayola;
            }

            &.approved {
              color: $blueNcs;
            }
          }
        }
      }
    }
  }

  @media (max-width: 767px) {
    padding-top: 10px;

    table {
      width: auto;
      display: block;
      padding-top: 5px;

      thead {
        display: block;

        tr {
          position: absolute;
          top: -9999px;
          left: -9999px;
        }
      }

      tbody {
        text-align: start;
        display: block;

        tr {
          display: block;

          &:last-child {
            border-bottom: 1px solid $alto;
          }

          td {
            padding: 2px;
            display:block;
            border: none;
            position: relative;
            padding-left: 45%;
            padding-right: 6px;
            min-height: 30px;

            &:first-child {
              border-left: unset;
              border-top: 1px solid $alto;
              padding-top: 20px;
              border-top-left-radius: unset;
            }

            &:last-child {
              border-right: unset;
              padding-bottom: 20px;
            }

            &::before {
              position: absolute;
              top: 0;
              left: 6px;
              width: 45%;
              padding-right: 10px;
              white-space: nowrap;
              text-align: center;
            }

            .mobile-header {
              position: absolute;
              left: 6px;
              padding-right: 10px;
              white-space: nowrap;
              text-align: center;
              padding-left: 10px;
            }

            .mobile-status {
              display: flex;

              small {
                padding-left: 10px;
              }
            }

            &.status {

              &.closed, &.canceled {
                color: $khaki;
              }
            }
          }
        }
      }
    }
  }
}
</style>
